/**
 * 声音发布弹窗组件
 * v10.1 新增：支持合成参数配置和实时试听
 * v10.4 新增：支持情感指令（instruction）选择
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Slider } from '@/shared/components/ui/slider'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Loader2,
  Play,
  Pause,
  Volume2,
  Mic2,
  Sparkles,
} from 'lucide-react'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService } from '@/modules/identity/services'
import type { Voice, VoiceInstructionsConfig, VoiceInstruction } from '@/modules/identity/types'
import { VOICE_SCENE_CONFIG, getAvailableInstructions } from '@/modules/identity/types'

interface VoicePublishDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  voice: Voice | null
  identityId: string
  onPublishSuccess?: () => void
  /** v10.4: 情感指令配置（从父组件传入，避免重复加载） */
  instructionsConfig?: VoiceInstructionsConfig | null
}

/**
 * 声音发布弹窗
 */
export function VoicePublishDialog({
  open,
  onOpenChange,
  voice,
  identityId,
  onPublishSuccess,
  instructionsConfig,
}: VoicePublishDialogProps) {
  const { showSuccess, showError } = useToast()
  const audioRef = useRef<HTMLAudioElement>(null)

  // 合成参数状态
  const [speechRate, setSpeechRate] = useState(1.0)
  const [pitchRate, setPitchRate] = useState(1.0)
  const [volume, setVolume] = useState(50)

  // v10.4: 情感指令状态
  const [instruction, setInstruction] = useState<string>('')

  // v10.4: 自己加载 targetModel（确保正确获取当前音色的训练模型）
  const [targetModel, setTargetModel] = useState<string>('')

  // 试听状态
  const [previewText, setPreviewText] = useState('你好，很高兴见到你！这是一段试听文本。')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)

  // 发布状态
  const [isPublishing, setIsPublishing] = useState(false)

  // v10.4: 加载当前音色的训练模型，获取 target_model
  useEffect(() => {
    async function loadTargetModel() {
      if (!voice || !open) {
        setTargetModel('')
        return
      }
      try {
        const response = await IdentityApiService.listVoiceModels(identityId, voice.id)
        // 找到激活的模型，获取其 target_model
        const activeModel = response.data.items?.find((m: { is_active: boolean }) => m.is_active)
        setTargetModel(activeModel?.target_model || '')
      } catch (error) {
        console.warn('Failed to load training models for voice:', error)
        setTargetModel('')
      }
    }
    loadTargetModel()
  }, [voice, identityId, open])

  // v10.4: 根据训练模型获取可用的指令列表
  const availableInstructions = useMemo(() => {
    if (!instructionsConfig || !targetModel) return []
    return getAvailableInstructions(instructionsConfig, targetModel)
  }, [instructionsConfig, targetModel])

  // v10.4: 判断是否支持情绪类指令（只有 Flash 支持）
  const supportsEmotion = useMemo(() => {
    if (!instructionsConfig?.cosyvoice || !targetModel) return false
    const supportedTypes = instructionsConfig.cosyvoice.model_support[targetModel] || []
    return supportedTypes.includes('emotion')
  }, [instructionsConfig, targetModel])

  // 初始化参数
  useEffect(() => {
    if (voice && open) {
      setSpeechRate(voice.speech_rate ?? 1.0)
      setPitchRate(voice.pitch_rate ?? 1.0)
      setVolume(voice.volume ?? 50)
      setInstruction(voice.instruction ?? '')
      setPreviewUrl(null)
    }
  }, [voice, open])

  // 试听函数
  const handlePreview = useCallback(async () => {
    if (!voice || !previewText.trim()) return

    setIsPreviewing(true)
    try {
      const response = await IdentityApiService.previewVoice(identityId, voice.id, {
        text: previewText,
        speech_rate: speechRate,
        pitch_rate: pitchRate,
        volume: volume,
      })
      setPreviewUrl(response.data.audio_url)

      // 自动播放 - 等待音频加载完成后再播放，避免 AbortError
      if (audioRef.current) {
        const audio = audioRef.current
        audio.src = response.data.audio_url
        audio.load()
        
        // 使用一次性事件监听器
        const handleCanPlay = () => {
          audio.play().catch((err) => {
            // 忽略 AbortError（用户快速点击导致的中断）
            if (err.name !== 'AbortError') {
              console.error('Play failed:', err)
            }
          })
          setIsPlaying(true)
          audio.removeEventListener('canplaythrough', handleCanPlay)
        }
        audio.addEventListener('canplaythrough', handleCanPlay)
      }
    } catch (err) {
      console.error('Preview failed:', err)
      showError('试听失败', '操作失败')
    } finally {
      setIsPreviewing(false)
    }
  }, [voice, identityId, previewText, speechRate, pitchRate, volume, showError])

  // 播放/暂停
  const togglePlay = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else if (previewUrl) {
        audioRef.current.play()
        setIsPlaying(true)
      }
    }
  }, [isPlaying, previewUrl])

  // 确认发布
  const handleConfirmPublish = useCallback(async () => {
    if (!voice) return

    setIsPublishing(true)
    try {
      await IdentityApiService.publishVoice(identityId, voice.id, {
        speech_rate: speechRate,
        pitch_rate: pitchRate,
        volume,
        // v10.4: 传递情感指令（可选）
        instruction: instruction || undefined,
      })
      showSuccess(`音色「${voice.name}」已发布`, '发布成功')
      onPublishSuccess?.()
    } catch (err) {
      console.error('Publish failed:', err)
      showError('发布失败', '操作失败')
    } finally {
      setIsPublishing(false)
    }
  }, [voice, identityId, speechRate, pitchRate, volume, instruction, showSuccess, showError, onPublishSuccess])

  if (!voice) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mic2 className="h-5 w-5 text-violet-500" />
            发布音色配置
          </DialogTitle>
          <DialogDescription>
            配置合成参数后发布，发布后该音色可被场景人格绑定使用。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* 音色信息 */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <span className="text-2xl">
              {VOICE_SCENE_CONFIG[voice.scene]?.icon || '🎤'}
            </span>
            <div>
              <p className="font-medium">{voice.name}</p>
              <p className="text-xs text-slate-500">
                {VOICE_SCENE_CONFIG[voice.scene]?.label || voice.scene}
                {voice.model_id && ` · ${voice.model_id}`}
              </p>
            </div>
          </div>

          {/* 合成参数 */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-slate-700">合成参数</h4>

            {/* 语速 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">语速</Label>
                <span className="text-sm text-slate-500">{speechRate.toFixed(1)}</span>
              </div>
              <Slider
                value={speechRate * 100}
                onValueChange={(v) => setSpeechRate(v / 100)}
                min={50}
                max={200}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0.5x (慢)</span>
                <span>1.0x</span>
                <span>2.0x (快)</span>
              </div>
            </div>

            {/* 音调 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">音调</Label>
                <span className="text-sm text-slate-500">{pitchRate.toFixed(1)}</span>
              </div>
              <Slider
                value={pitchRate * 100}
                onValueChange={(v) => setPitchRate(v / 100)}
                min={50}
                max={200}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0.5x (低)</span>
                <span>1.0x</span>
                <span>2.0x (高)</span>
              </div>
            </div>

            {/* 音量 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm flex items-center gap-1">
                  <Volume2 className="h-4 w-4" />
                  音量
                </Label>
                <span className="text-sm text-slate-500">{volume}</span>
              </div>
              <Slider
                value={volume}
                onValueChange={(v) => setVolume(v)}
                min={0}
                max={100}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0 (静音)</span>
                <span>50</span>
                <span>100 (最大)</span>
              </div>
            </div>
          </div>

          {/* v10.4: 情感指令选择 */}
          {availableInstructions.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-500" />
                情感指令
                <span className="text-xs font-normal text-slate-400">（可选）</span>
              </h4>
              <Select
                value={instruction || '__none__'}
                onValueChange={(v) => setInstruction(v === '__none__' ? '' : v)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="选择情感或方言效果" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">无（默认）</SelectItem>
                  {/* 情绪类 */}
                  {supportsEmotion && instructionsConfig?.cosyvoice.emotion && instructionsConfig.cosyvoice.emotion.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>情绪类</SelectLabel>
                      {instructionsConfig.cosyvoice.emotion.map((item: VoiceInstruction) => (
                        <SelectItem key={item.id} value={item.instruction}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {/* 方言类 */}
                  {instructionsConfig?.cosyvoice.dialect && instructionsConfig.cosyvoice.dialect.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>方言类</SelectLabel>
                      {instructionsConfig.cosyvoice.dialect.map((item: VoiceInstruction) => (
                        <SelectItem key={item.id} value={item.instruction}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              {!supportsEmotion && (
                <p className="text-xs text-amber-600">
                  💡 当前模型仅支持方言类指令，情绪类指令需使用 Flash 模型训练
                </p>
              )}
            </div>
          )}

          {/* 试听区域 */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-700">试听效果</h4>
            <div className="flex gap-2">
              <Input
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                placeholder="输入试听文本..."
                className="flex-1"
                maxLength={200}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handlePreview}
                disabled={isPreviewing || !previewText.trim()}
                title="试听"
              >
                {isPreviewing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
            </div>
            {previewUrl && (
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={togglePlay}
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
                <audio
                  ref={audioRef}
                  onEnded={() => setIsPlaying(false)}
                  onPause={() => setIsPlaying(false)}
                  className="flex-1"
                  controls
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPublishing}
          >
            取消
          </Button>
          <Button
            onClick={handleConfirmPublish}
            disabled={isPublishing}
          >
            {isPublishing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                发布中...
              </>
            ) : (
              '确认发布'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
